import React, { useState, useEffect } from "react";
import { Card, Table, Input, Button, Space, Tooltip } from "antd";
import { EyeOutlined, EditOutlined } from "@ant-design/icons";
import Modal from "../../components/Modal";
import { useGet } from "../../hooks/useApi";
import { useDebounce } from "../../hooks/useDebounce";
import { API_ENDPOINTS, API_BASE_URL } from "../../config/api";
import Toast from "../../utils/toast";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDynamicApi } from "../../hooks/useApi";

const EmailTemplateManager = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isViewMode, setIsViewMode] = useState(false);

  // State for form data
  const [formData, setFormData] = useState({
    subject: "",
    template_content: "",
  });

  // Search and pagination states
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [templatesList, setTemplatesList] = useState([]);

  const [isSaving, setIsSaving] = useState(false);
  const { execute: getTemplateById } = useDynamicApi("GET");
  const { execute: updateTemplate } = useDynamicApi("PUT");

  // Build API URL with pagination and search
  const buildApiUrl = () => {
    let url = `${API_ENDPOINTS.EMAIL_TEMPLATE_LIST}?page=${currentPage}&page_size=${pageSize}`;

    // Add search parameter
    if (debouncedSearchTerm) {
      url += `&search=${encodeURIComponent(debouncedSearchTerm)}`;
    }

    return url;
  };

  // Use GET for listing email templates with pagination and search
  const apiUrl = buildApiUrl();
  const { loading: loadingTemplates, execute: fetchTemplates } = useGet(
    apiUrl,
    false,
  );

  // Load data function
  const loadData = async () => {
    try {
      const response = await fetchTemplates();
      if (response) {
        setTemplatesList(response.data || []);
        setTotalPages(response.pagination?.total_pages || 1);
        setTotalCount(response.pagination?.total_count || 0);
      }
    } catch (error) {
      console.error("Error loading data:", error);
      Toast.error(error.message || t("common.error"));
    }
  };

  // Initial data fetch
  useEffect(() => {
    loadData();
  }, [currentPage, pageSize]);

  // Handle search - triggers when debounced value changes
  useEffect(() => {
    setCurrentPage(1); // Reset to first page on search
    loadData();
  }, [debouncedSearchTerm]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Reset form data
  const resetForm = () => {
    setFormData({
      subject: "",
      template_content: "",
    });
    setSelectedTemplate(null);
    setIsViewMode(false);
  };

  // Function to get token
  const getToken = () => {
    return (
      localStorage.getItem("adminToken") ||
      localStorage.getItem("access_token") ||
      localStorage.getItem("token") ||
      sessionStorage.getItem("adminToken") ||
      sessionStorage.getItem("access_token")
    );
  };

  // Custom PUT function for update
  const customPut = async (url, data) => {
    const token = getToken();
    if (!token) {
      Toast.error(t("common.error"));
      throw new Error("Authentication token not found");
    }

    try {
      const response = await fetch(`${API_BASE_URL}${url}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || t("emailManager.failedUpdate"));
      }

      return result;
    } catch (error) {
      console.error("PUT request failed:", error);
      throw error;
    }
  };

  // Custom GET by ID function
  const customGetById = async (url) => {
    const token = getToken();
    if (!token) {
      Toast.error(t("common.error"));
      throw new Error("Authentication token not found");
    }

    try {
      const response = await fetch(`${API_BASE_URL}${url}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || t("emailManager.failedFetchDetails"));
      }

      return result;
    } catch (error) {
      console.error("GET by ID request failed:", error);
      throw error;
    }
  };

  // Navigate to view page
  const openViewPage = (id) => {
    navigate(`/email-templates/details/${id}`);
  };

  // Open modal for edit (keep for backward compatibility)
  // const openEditModal = async (template) => {
  //   try {
  //     setSelectedTemplate(template);

  //     const endpoint = `${API_ENDPOINTS.EMAIL_TEMPLATE_BY_ID}/${template.id}`;
  //     const response = await customGetById(endpoint);

  //     if (response?.status === true && response.data) {
  //       setSelectedTemplate(response.data);
  //       setFormData({
  //         subject: response.data.subject || "",
  //         template_content: response.data.template_content || "",
  //       });
  //       setIsViewMode(false);
  //       setIsModalOpen(true);
  //     } else {
  //       Toast.error(t("emailManager.failedFetchDetails"));
  //     }
  //   } catch (error) {
  //     console.error("Error fetching template details:", error);
  //     Toast.error(error.message || t("emailManager.failedFetchDetails"));
  //   }
  // };

  const openEditModal = async (template) => {
    try {
      setSelectedTemplate(template);
      const endpoint = `${API_ENDPOINTS.EMAIL_TEMPLATE_BY_ID}/${template.id}`;
      const response = await getTemplateById(endpoint);

      if (response?.status === true && response.data) {
        setSelectedTemplate(response.data);
        setFormData({
          subject: response.data.subject || "",
          template_content: response.data.template_content || "",
        });
        setIsViewMode(false);
        setIsModalOpen(true);
      } else {
        Toast.error(t("emailManager.failedFetchDetails"));
      }
    } catch (error) {
      console.error("Error fetching template details:", error);
      Toast.error(error.message || t("emailManager.failedFetchDetails"));
    }
  };

  // Handle update template
  // const handleUpdate = async (e) => {
  //   e.preventDefault();

  //   if (!selectedTemplate?.id) {
  //     Toast.error(t("emailManager.idNotFound"));
  //     return;
  //   }

  //   // Validation
  //   if (!formData.subject.trim()) {
  //     Toast.error(t("emailManager.subjectRequired"));
  //     return;
  //   }

  //   if (!formData.template_content.trim()) {
  //     Toast.error(t("emailManager.contentRequired"));
  //     return;
  //   }

  //   setIsSaving(true);
  //   try {
  //     const endpoint = `${API_ENDPOINTS.EMAIL_TEMPLATE_UPDATE}/${selectedTemplate.id}`;
  //     const response = await customPut(endpoint, formData);

  //     if (response?.status === true) {
  //       Toast.success(response?.message || t("emailManager.updatedSuccess"));
  //       await loadData();
  //       setIsModalOpen(false);
  //       resetForm();
  //     } else {
  //       Toast.error(response?.message || t("emailManager.failedUpdate"));
  //     }
  //   } catch (error) {
  //     console.error("Error updating template:", error);
  //     Toast.error(error.message || t("emailManager.failedUpdate"));
  //   } finally {
  //     setIsSaving(false);
  //   }
  // };

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!selectedTemplate?.id) {
      Toast.error(t("emailManager.idNotFound"));
      return;
    }

    // Validation
    if (!formData.subject.trim()) {
      Toast.error(t("emailManager.subjectRequired"));
      return;
    }
    if (!formData.template_content.trim()) {
      Toast.error(t("emailManager.contentRequired"));
      return;
    }

    setIsSaving(true);
    try {
      const endpoint = `${API_ENDPOINTS.EMAIL_TEMPLATE_UPDATE}/${selectedTemplate.id}`;
      const response = await updateTemplate(endpoint, formData); // second arg is body for PUT

      if (response?.status === true) {
        Toast.success(response?.message || t("emailManager.updatedSuccess"));
        await loadData();
        setIsModalOpen(false);
        resetForm();
      } else {
        Toast.error(response?.message || t("emailManager.failedUpdate"));
      }
    } catch (error) {
      console.error("Error updating template:", error);
      Toast.error(error.message || t("emailManager.failedUpdate"));
    } finally {
      setIsSaving(false);
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === "nl" ? "nl-NL" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // List of available variables for each template type
  const getTemplateVariables = (templateSlug) => {
    const variables = {
      "reset-password": [
        { name: "{{ name }}", description: t("emailManager.varUserName") },
        {
          name: "{{ reset_link }}",
          description: t("emailManager.varResetLink"),
        },
      ],
      "login-otp": [
        { name: "{{ user_name }}", description: t("emailManager.varUserName") },
        { name: "{{ otp }}", description: t("emailManager.varOtp") },
        {
          name: "{{ expiry_minutes }}",
          description: t("emailManager.varExpiryMinutes"),
        },
        {
          name: "{{ current_year }}",
          description: t("emailManager.varCurrentYear"),
        },
      ],
    };

    return (
      variables[templateSlug] || [
        { name: "{{ name }}", description: t("emailManager.varUserName") },
        { name: "{{ email }}", description: t("emailManager.varEmail") },
      ]
    );
  };

  const columns = [
    {
      title: t("emailManager.title"),
      dataIndex: "title",
      key: "title",
      render: (title) => <strong>{title || "N/A"}</strong>,
    },
    {
      title: t("emailManager.subject"),
      dataIndex: "subject",
      key: "subject",
      render: (subject) => (
        <span className="subject-text">{subject || "N/A"}</span>
      ),
    },
    {
      title: t("emailManager.slug"),
      dataIndex: "slug",
      key: "slug",
      render: (slug) => <span className="slug-badge">{slug || "N/A"}</span>,
    },
    {
      title: t("common.lastUpdated"),
      key: "updated_at",
      render: (_, record) => formatDate(record.updated_at || record.created_at),
    },
    {
      title: t("common.actions"),
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title={t("common.view")}>
            <Button
              className="btn-view"
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => openViewPage(record.id)}
            />
          </Tooltip>
          <Tooltip title={t("common.edit")}>
            <Button
              className="btn-edit"
              type="primary"
              size="small"
              style={{ background: "#52c41a", borderColor: "#52c41a" }}
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card title={t("emailManager.title")}>
        <Input
          placeholder={t("emailManager.searchPlaceholder")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          allowClear
          style={{ maxWidth: 400, marginBottom: 16 }}
          disabled={loadingTemplates}
        />
        <div className="table-responsive">
          <Table
            dataSource={templatesList}
            columns={columns}
            rowKey="id"
            loading={loadingTemplates}
            locale={{
              emptyText: searchTerm
                ? t("emailManager.noMatchingTemplates")
                : t("emailManager.noTemplatesFound"),
            }}
            pagination={{
              current: currentPage,
              pageSize,
              total: totalCount,
              showSizeChanger: true,
              pageSizeOptions: ["5", "10", "20", "50", "100"],
              showTotal: (total) =>
                `${t("common.showing")} ${(currentPage - 1) * pageSize + 1} ${t("common.to")} ${Math.min(currentPage * pageSize, total)} ${t("common.of")} ${total} ${t("emailManager.templates")}`,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size || 10);
              },
            }}
          />
        </div>
      </Card>

      {/* Edit Modal */}
      {isModalOpen && !isViewMode && selectedTemplate && (
        <Modal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            resetForm();
          }}
          title={`${t("emailManager.editTitle")}: ${selectedTemplate.title}`}
          onConfirm={handleUpdate}
          confirmText={
            isSaving ? t("emailManager.saving") : t("emailManager.saveChanges")
          }
          showConfirm={true}
          size="xlarge"
          confirmDisabled={isSaving}
        >
          <div className="email-template-modal">
            <div className="template-header">
              <div className="template-info">
                <h3 className="template-title">{selectedTemplate.title}</h3>
                <div className="template-meta">
                  <span className="meta-item">
                    <strong>{t("emailManager.slug")}:</strong>{" "}
                    {selectedTemplate.slug}
                  </span>
                  <span className="meta-item">
                    <strong>{t("common.createdAt")}:</strong>{" "}
                    {formatDate(selectedTemplate.created_at)}
                  </span>
                  <span className="meta-item">
                    <strong>{t("common.lastUpdated")}:</strong>{" "}
                    {formatDate(selectedTemplate.updated_at)}
                  </span>
                </div>
              </div>
            </div>

            <div className="template-variables">
              <h4>{t("emailManager.availableVariables")}:</h4>
              <div className="variables-list">
                {getTemplateVariables(selectedTemplate.slug).map(
                  (variable, index) => (
                    <div key={index} className="variable-item">
                      <code className="variable-name">{variable.name}</code>
                      <span className="variable-desc">
                        {variable.description}
                      </span>
                    </div>
                  ),
                )}
              </div>
              <div className="variables-help">
                <small>{t("emailManager.variablesHelp")}</small>
              </div>
            </div>

            <form onSubmit={handleUpdate} className="template-form">
              <div className="form-group">
                <label htmlFor="subject">
                  {t("emailManager.subject")}{" "}
                  <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  placeholder={t("emailManager.enterSubject")}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="template_content">
                  {t("emailManager.templateContent")}{" "}
                  <span className="required">*</span>
                </label>
                <>
                  <textarea
                    id="template_content"
                    name="template_content"
                    value={formData.template_content}
                    onChange={handleInputChange}
                    placeholder={t("emailManager.enterTemplateContent")}
                    rows="15"
                    style={{
                      width: "100%",
                      padding: "15px",
                      fontFamily: "monospace",
                      fontSize: "13px",
                      lineHeight: "1.4",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      resize: "vertical",
                      whiteSpace: "pre",
                    }}
                  />
                  <div className="form-help">
                    {t("emailManager.templateHelp")}
                  </div>

                  <div className="html-preview-section">
                    <h4>{t("emailManager.livePreview")}:</h4>
                    <div className="preview-container">
                      <div
                        className="live-preview"
                        style={{
                          border: "1px solid #eee",
                          padding: "20px",
                          borderRadius: "4px",
                          maxHeight: "300px",
                          overflowY: "auto",
                          backgroundColor: "#f9f9f9",
                        }}
                        dangerouslySetInnerHTML={{
                          __html:
                            formData.template_content ||
                            `<p><em>${t("emailManager.noContentToPreview")}</em></p>`,
                        }}
                      />
                    </div>
                  </div>
                </>
              </div>
            </form>
          </div>
        </Modal>
      )}
    </>
  );
};

export default EmailTemplateManager;
